// AGC: Input - Form input component for Classic Navy Theme
// AGC: Clean white inputs with subtle borders
import React, { forwardRef } from 'react'

type InputSize = 'sm' | 'md' | 'lg'

interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  size?: InputSize
  label?: string
  error?: string
  hint?: string
  leftIcon?: React.ReactNode
  rightIcon?: React.ReactNode
  fullWidth?: boolean
}

/**
 * AGC: Input - Clean input component for Classic Navy Theme
 * AGC: Features:
 *   - White background with subtle border
 *   - Dark gray text
 *   - Navy focus ring
 *   - Label, error, and hint text support
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(({
  size = 'md',
  label,
  error,
  hint,
  leftIcon,
  rightIcon,
  fullWidth = true,
  className = '',
  id,
  ...props
}, ref) => {
  // AGC: Generate ID if not provided
  const inputId = id || `input-${Math.random().toString(36).substr(2, 9)}`

  // AGC: Size classes
  const sizeClasses: Record<InputSize, string> = {
    sm: 'py-1.5 text-sm',
    md: 'py-2.5 text-sm',
    lg: 'py-3 text-base',
  }

  const iconSizeClasses: Record<InputSize, string> = {
    sm: 'w-4 h-4',
    md: 'w-4 h-4',
    lg: 'w-5 h-5',
  }

  // AGC: Base input classes for Classic Navy theme
  const baseClasses = `
    w-full rounded-lg
    bg-white dark:bg-slate-800
    border transition-all duration-200
    text-gray-900 dark:text-white
    placeholder:text-gray-400 dark:placeholder:text-gray-500
    focus:outline-none
    disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-50 dark:disabled:bg-slate-900
  `

  // AGC: Border and focus classes based on error state
  const stateClasses = error
    ? 'border-red-300 focus:border-red-500 focus:ring-2 focus:ring-red-500/20'
    : 'border-gray-200 dark:border-slate-600 focus:border-navy-400 focus:ring-2 focus:ring-navy-500/20'

  // AGC: Padding classes based on icons
  const paddingClasses = `
    ${leftIcon ? 'pl-10' : 'pl-4'}
    ${rightIcon ? 'pr-10' : 'pr-4'}
  `

  return (
    <div className={`${fullWidth ? 'w-full' : ''} ${className}`}>
      {/* AGC: Label */}
      {label && (
        <label 
          htmlFor={inputId}
          className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5"
        >
          {label}
        </label>
      )}

      {/* AGC: Input container */}
      <div className="relative">
        {/* AGC: Left icon */}
        {leftIcon && (
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
            <span className={iconSizeClasses[size]}>{leftIcon}</span>
          </div>
        )}

        {/* AGC: Input element */}
        <input
          ref={ref}
          id={inputId}
          className={`
            ${baseClasses}
            ${stateClasses}
            ${sizeClasses[size]}
            ${paddingClasses}
          `.replace(/\s+/g, ' ').trim()}
          {...props}
        />

        {/* AGC: Right icon */}
        {rightIcon && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
            <span className={iconSizeClasses[size]}>{rightIcon}</span>
          </div>
        )}
      </div>

      {/* AGC: Error message */}
      {error && (
        <p className="mt-1.5 text-sm text-red-600 dark:text-red-400">
          {error}
        </p>
      )}

      {/* AGC: Hint text */}
      {hint && !error && (
        <p className="mt-1.5 text-sm text-gray-500 dark:text-gray-400">
          {hint}
        </p>
      )}
    </div>
  )
})

Input.displayName = 'Input'

/**
 * AGC: TextArea - Clean textarea component for Classic Navy Theme
 */
interface TextAreaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string
  error?: string
  hint?: string
  fullWidth?: boolean
}

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(({
  label,
  error,
  hint,
  fullWidth = true,
  className = '',
  id,
  rows = 4,
  ...props
}, ref) => {
  const textareaId = id || `textarea-${Math.random().toString(36).substr(2, 9)}`

  const baseClasses = `
    w-full px-4 py-2.5 rounded-lg
    bg-white dark:bg-slate-800
    border transition-all duration-200
    text-gray-900 dark:text-white
    placeholder:text-gray-400 dark:placeholder:text-gray-500
    focus:outline-none
    disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-50 dark:disabled:bg-slate-900
    resize-none
  `

  const stateClasses = error
    ? 'border-red-300 focus:border-red-500 focus:ring-2 focus:ring-red-500/20'
    : 'border-gray-200 dark:border-slate-600 focus:border-navy-400 focus:ring-2 focus:ring-navy-500/20'

  return (
    <div className={`${fullWidth ? 'w-full' : ''} ${className}`}>
      {label && (
        <label 
          htmlFor={textareaId}
          className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5"
        >
          {label}
        </label>
      )}

      <textarea
        ref={ref}
        id={textareaId}
        rows={rows}
        className={`${baseClasses} ${stateClasses}`.replace(/\s+/g, ' ').trim()}
        {...props}
      />

      {error && (
        <p className="mt-1.5 text-sm text-red-600 dark:text-red-400">{error}</p>
      )}

      {hint && !error && (
        <p className="mt-1.5 text-sm text-gray-500 dark:text-gray-400">{hint}</p>
      )}
    </div>
  )
})

TextArea.displayName = 'TextArea'

export default Input
